import { create } from 'zustand';
import { SpellCatalogueState } from '../types/spellCatalogue.types';
const STORAGE_KEY = 'usagi_spell_catalogue';

const DEFAULT_STATE: SpellCatalogueState = {
  unlockedSpells: [],
  masteryXp: {},
  equippedActive: [],
  equippedPassive: [],
};

function load(): SpellCatalogueState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<SpellCatalogueState>;
      return {
        unlockedSpells: parsed.unlockedSpells ?? [],
        masteryXp: parsed.masteryXp ?? {},
        equippedActive: parsed.equippedActive ?? [],
        equippedPassive: parsed.equippedPassive ?? [],
      };
    }
  } catch {
    /* ignore */
  }
  return DEFAULT_STATE;
}

function persist(state: SpellCatalogueState): void {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      unlockedSpells: state.unlockedSpells,
      masteryXp: state.masteryXp,
      equippedActive: state.equippedActive,
      equippedPassive: state.equippedPassive,
    }),
  );
}

interface SpellCatalogueActions {
  /** Returns true if the spell was just added. */
  unlock: (spellId: string) => boolean;
  isUnlocked: (spellId: string) => boolean;
  getUnlocked: () => string[];
  /** Extends the array if needed. */
  setEquippedActive: (slotIndex: number, spellId: string | null) => void;
  setEquippedPassive: (slotIndex: number, spellId: string | null) => void;
  /** Mastery XP gain (1-5 system). */
  gainMasteryXp: (spellId: string, amount: number) => void;
  /** Full reset (debug / new profile). */
  resetCatalogue: () => void;
}

export const useSpellCatalogStore = create<
  SpellCatalogueState & SpellCatalogueActions
>((set, get) => ({
  ...load(),

  unlock: (spellId) => {
    if (get().unlockedSpells.includes(spellId)) {
      return false;
    }
    set((s) => {
      const next = {
        ...s,
        unlockedSpells: [...s.unlockedSpells, spellId],
      };
      persist(next);
      return next;
    });
    return true;
  },

  isUnlocked: (spellId) => get().unlockedSpells.includes(spellId),

  getUnlocked: () => get().unlockedSpells,

  setEquippedActive: (slotIndex, spellId) => {
    set((s) => {
      const arr = [...s.equippedActive];
      while (arr.length <= slotIndex) {
        arr.push(null);
      }
      arr[slotIndex] = spellId;
      const next = { ...s, equippedActive: arr };
      persist(next);
      return next;
    });
  },

  setEquippedPassive: (slotIndex, spellId) => {
    set((s) => {
      const arr = [...s.equippedPassive];
      while (arr.length <= slotIndex) {
        arr.push(null);
      }
      arr[slotIndex] = spellId;
      const next = { ...s, equippedPassive: arr };
      persist(next);
      return next;
    });
  },

  gainMasteryXp: (spellId, amount) => {
    set((s) => {
      const next = {
        ...s,
        masteryXp: {
          ...s.masteryXp,
          [spellId]: (s.masteryXp[spellId] ?? 0) + amount,
        },
      };
      persist(next);
      return next;
    });
  },

  resetCatalogue: () => {
    set(DEFAULT_STATE);
    persist(DEFAULT_STATE);
  },
}));
